import React from 'react';

import AdmissionDocumentButtons from './AdmissionDocumentButtons';
import { LaneSubsection } from './LaneSubsection';
import { ADMISSION_STATUS_LABELS } from '../../constants/admission';
import { LAB_CONTEXT_KIND } from '../../constants/patientCard';
import { readLane } from '../../utils/patientCardLanes';
import { formatDateIST, formatDayIST } from '../../utils/formatIST';
import { useTheme } from '../../theme/ThemeContext';
import { VisitLabHistorySection } from '../visit/history/VisitLabHistorySection';

/**
 * IpdTimelineRow — F8. Renders one IPD admission row inside the unified
 * patient timeline (F6's `TimelineRow` slot, mockup `card-timeline.html`
 * Variant A). Frozen prop signature (`TimelineRow.jsx` JSDoc, do not
 * redesign at integration), `labs` added ADDITIVELY per bk-1128 below:
 *
 *   { admission: row.item, documents: sections.documents, labs: sections.labs, expanded, onToggle }
 *
 * `admission` — the resolved `AdmissionResponse` (§1.2 `admissions` item,
 * carrying the added `occurred_at`/`ends_at`/`day_ist` fields — `day_ist`
 * is read here for the admit date, never a slice of a timestamp).
 * `documents` — the FULL `sections.documents` SectionObject (one entry per
 * admission on the patient); this row matches its OWN `admission_id` inside
 * it. Availability comes ONLY from that descriptor list (INV-17/DR-5) — see
 * `AdmissionDocumentButtons`, which never probes a PDF route to find out
 * whether a document exists.
 *
 * IPD module off / a documents-lane read failure ⇒ this row still renders
 * the admission's own facts; only the document-buttons area is omitted —
 * never an error, never a call to an IPD route.
 *
 * `labs` (bk-1128) — the FULL `sections.labs` SectionObject, ADDITIVE to the
 * frozen four above (`TimelineRow.jsx` now passes `labs={sections?.labs}`
 * alongside `documents={sections?.documents}` — same "hand the row its own
 * slice of the already-fetched map" shape, no new request). This row filters
 * to its OWN admission via the server-resolved `context` (`kind ===
 * IPD_ADMISSION && ref_id === admission_id`) — never an inferred visit-id
 * shape, same rule `OpdTimelineRow` applies for `OPD_VISIT`. Reused verbatim:
 * `VisitLabHistorySection` + `LabReportActions`, `reportOnly` — this card
 * holds a fixed in-memory snapshot with no refetch path, so a write here
 * would report success against stale state. A labs-lane read failure never
 * renders as "no lab orders" (Q2 / QA D3) — routed through the same
 * `LaneSubsection` guard the OPD row uses.
 */
export default function IpdTimelineRow({ admission, documents, labs, expanded, onToggle }) {
  const { isDark } = useTheme();
  const admissionId = admission?.admission_id;
  const rowKey = `ADMISSION-${admissionId}`;

  const admitLabel = formatDayIST(admission?.day_ist);
  const dischargeLabel = admission?.ends_at ? formatDateIST(admission.ends_at) : 'Ongoing';
  const wardBed = [admission?.ward_name, admission?.bed_label].filter(Boolean).join(' · ') || '—';
  const statusLabel = ADMISSION_STATUS_LABELS[admission?.status] || admission?.status || '—';

  // Descriptors through the ONE shared lane guard (Q2): an unreadable
  // documents lane yields no descriptors, so this row omits the button area
  // entirely rather than rendering it as "no documents exist".
  const { items: documentEntries } = readLane(documents);
  const docList = documentEntries.find((d) => d?.admission_id === admissionId)?.documents || [];

  // ── Labs (bk-1128) — server-resolved `context`, never an inferred
  // visit/admission shape (same F7 data rule `OpdTimelineRow` applies). ────
  const labsLane = readLane(labs);
  const { items: labItems } = labsLane;
  const labOrders = labItems
    .filter((o) => o.context?.kind === LAB_CONTEXT_KIND.IPD_ADMISSION && o.context?.ref_id === admissionId);

  return (
    <div
      data-testid={`timeline-row-${rowKey}`}
      className="rounded-md border border-slate-200 dark:border-slate-700 border-l-[3px] border-l-blue-500 bg-white dark:bg-slate-800 overflow-hidden"
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        data-testid={`timeline-row-toggle-${rowKey}`}
        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left hover:bg-slate-50 dark:hover:bg-slate-700/60"
      >
        <div className="flex flex-col gap-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="rounded-full px-2 py-0.5 text-[10px] font-bold bg-blue-600 text-white">IPD</span>
            <span className="text-xs font-bold text-slate-800 dark:text-slate-100">{admitLabel} → {dischargeLabel}</span>
            <span className="text-[10.5px] font-semibold px-1.5 py-0.5 rounded-full border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 whitespace-nowrap">
              {statusLabel}
            </span>
          </div>
          <span className="text-[11px] text-slate-500 dark:text-slate-400 truncate">🛏 {wardBed}</span>
        </div>
        <span className={`text-[10px] text-slate-400 transition-transform flex-none ${expanded ? 'rotate-180' : ''}`}>
          ▼
        </span>
      </button>
      {expanded && (
        <div data-testid={`timeline-row-body-${rowKey}`} className="px-3 pb-3 pt-1 bg-slate-50 dark:bg-slate-900/40">
          <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-[11px] mb-3">
            <div>
              <dt className="text-slate-400 dark:text-slate-500">Admit date</dt>
              <dd className="text-slate-700 dark:text-slate-200 font-semibold">{admitLabel}</dd>
            </div>
            <div>
              <dt className="text-slate-400 dark:text-slate-500">Discharge date</dt>
              <dd className="text-slate-700 dark:text-slate-200 font-semibold">{dischargeLabel}</dd>
            </div>
            <div>
              <dt className="text-slate-400 dark:text-slate-500">Ward / Bed</dt>
              <dd className="text-slate-700 dark:text-slate-200 font-semibold">{wardBed}</dd>
            </div>
            <div>
              <dt className="text-slate-400 dark:text-slate-500">Status</dt>
              <dd className="text-slate-700 dark:text-slate-200 font-semibold">{statusLabel}</dd>
            </div>
          </dl>
          {labOrders.length > 0 ? (
            <VisitLabHistorySection orders={labOrders} isDark={isDark} reportOnly />
          ) : (
            <LaneSubsection
              title="🧪 Lab tests"
              isDark={isDark}
              lane={labsLane}
              laneName="labs"
              emptyText="No lab orders for this admission."
            />
          )}
          {docList.length > 0 && (
            <AdmissionDocumentButtons admissionId={admissionId} documents={docList} />
          )}
        </div>
      )}
    </div>
  );
}
